import { Injectable, inject } from '@angular/core';
import { CanActivate, CanActivateChild, ActivatedRouteSnapshot, Router } from '@angular/router';
import { PlatformConfigService } from '../services/platform-config.service';
import { PlatformType } from '../models/platform.model';
import { APP_PATHS } from '@core/constants/platform-config.constants';
import { Store } from '@ngrx/store';
import * as UserAuthSelectors from '../../store/user/auth/user-auth.selectors';
import * as UserAuthActions from '../../store/user/auth/user-auth.actions';
import { Observable, filter, map, take, of } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class PlatformGuard implements CanActivate, CanActivateChild {
  private store = inject(Store);
  private user = this.store.selectSignal(UserAuthSelectors.selectUserDetails);
  private isLoading = this.store.selectSignal(UserAuthSelectors.selectUserAuthLoading);

  constructor(
    private platformConfigService: PlatformConfigService,
    private router: Router
  ) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
    if (!this.user() && !this.isLoading()) {
      this.store.dispatch(UserAuthActions.loadUserDetails());
    }

    const platformId = route.data['platform'] as PlatformType;

    if (!platformId) {
      return this.guardSelectPlatform$();
    }

    return this.guardPlatformLayout$(platformId);
  }

  /**
   * Re-runs location / access checks on every child navigation (parent {@link CanActivate} does not).
   */
  canActivateChild(childRoute: ActivatedRouteSnapshot): Observable<boolean> {
    if (!this.user() && !this.isLoading()) {
      this.store.dispatch(UserAuthActions.loadUserDetails());
    }
    const platformId = this.findPlatformInAncestors(childRoute);
    if (!platformId) {
      return of(true);
    }
    return this.guardPlatformLayout$(platformId);
  }

  private findPlatformInAncestors(route: ActivatedRouteSnapshot | null): PlatformType | undefined {
    let r: ActivatedRouteSnapshot | null = route;
    while (r) {
      const id = r.data['platform'] as PlatformType | undefined;
      if (id) {
        return id;
      }
      r = r.parent;
    }
    return undefined;
  }

  /** `select-platform` route: no `data.platform`. */
  private guardSelectPlatform$(): Observable<boolean> {
    return this.store.select(UserAuthSelectors.selectUserAuthState).pipe(
      filter((state) => !state.isLoading && (!!state.userDetails || !!state.error)),
      map(() => {
        if (this.platformConfigService.isNoLocationAssignedState()) {
          return true;
        }
        const autoRedirectPlatform = this.platformConfigService.getAutoRedirectPlatform();

        if (autoRedirectPlatform) {
          this.platformConfigService.setCurrentPlatform(autoRedirectPlatform.id);
          this.router.navigate([autoRedirectPlatform.defaultRoute]);
          return false;
        }
        return true;
      }),
      take(1)
    );
  }

  /** Any route under `/{gh|gse|...}` with `data.platform`. */
  private guardPlatformLayout$(platformId: PlatformType): Observable<boolean> {
    return this.store.select(UserAuthSelectors.selectUserAuthState).pipe(
      filter((state) => !state.isLoading && (!!state.userDetails || !!state.error)),
      map(() => {
        if (this.platformConfigService.isNoLocationAssignedState()) {
          this.router.navigate([`/${APP_PATHS.SELECT_PLATFORM}`]);
          return false;
        }

        const hasAccess = this.platformConfigService.canAccessPlatform(platformId);

        if (!hasAccess) {
          this.router.navigate([`/${APP_PATHS.SELECT_PLATFORM}`]);
          return false;
        }

        if (this.platformConfigService.currentPlatform$() !== platformId) {
          this.platformConfigService.setCurrentPlatform(platformId);
        }

        return true;
      }),
      take(1)
    );
  }
}
